// src/components/ui/NaranjaPlusRequiredModal.tsx
// 🍊 MODAL DE BLOQUEO NARANJA PLUS (multi-dispositivo y nube)
//
// Se monta una sola vez en la raíz de la app y aparece cuando:
//   1. Un usuario SIN Naranja Plus inicia sesión en un SEGUNDO dispositivo
//      (este equipo no tiene datos locales del negocio).
//   2. Intenta usar una función de nube/sincronización (botón de sync, pujar en
//      subastas, abrir la tienda digital, etc.) → vía `requireNaranjaPlus()`.
//
// El botón lleva directo a la Billetera Flip / Configuración.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Wallet, Cloud, Store, Smartphone } from 'lucide-react';
import { db } from '@/db/db';
import { useAuthStore } from '@/store/authStore';
import { NARANJA_PLUS_MODAL_TEXT, onNaranjaPlusRequired } from '@/lib/naranjaPlusModal';
import { NARANJA_PLUS_DEV_OVERRIDE } from '@/lib/naranjaPlusRules';
import { getLocalSubscriptionState } from '@/lib/naranjaPlus';

const DEVICE_MARKER = 'niiz_device_first_seen';

/** ¿Este navegador es un SEGUNDO dispositivo? (no tiene datos locales del negocio) */
const isSecondDevice = async (): Promise<boolean> => {
  try {
    const settingsCount = await db.settings.count();
    const salesCount = await db.sales.count();
    // Sin configuración local ni ventas = equipo nuevo para este negocio.
    if (settingsCount === 0 && salesCount === 0) {
      if (!localStorage.getItem(DEVICE_MARKER)) {
        localStorage.setItem(DEVICE_MARKER, new Date().toISOString());
      }
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

export const NaranjaPlusRequiredModal = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [motivo, setMotivo] = useState<string | null>(null);

  // 1) Apertura por petición de cualquier función de nube.
  useEffect(() => {
    return onNaranjaPlusRequired((why) => {
      setMotivo(why || null);
      setIsOpen(true);
    });
  }, []);

  // 2) Apertura automática al detectar un SEGUNDO dispositivo sin suscripción.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void (async () => {
      try {
        const state = await getLocalSubscriptionState();
        if (state.isActive) return;                      // suscriptor: sin aviso
        const second = await isSecondDevice();
        if (!cancelled && second) {
          setMotivo('Iniciaste sesión en un segundo dispositivo sin Naranja Plus activo.');
          setIsOpen(true);
        }
      } catch {
        /* silencio: nunca bloquear la app por esto */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // 🛠️ Desarrollo: todas las cuentas operan con Naranja Plus; no se bloquea.
  if (NARANJA_PLUS_DEV_OVERRIDE) return null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => setIsOpen(false)} />

      <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-[2rem] border-2 border-orange-300 dark:border-orange-900 shadow-2xl overflow-hidden">
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          aria-label="Cerrar"
        >
          <X size={18} />
        </button>

        <div className="p-7">
          <div className="w-14 h-14 rounded-2xl bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-2xl mb-4">
            🍊
          </div>

          <h2 className="text-xl font-black text-slate-900 dark:text-white mb-2">Naranja Plus</h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-snug">{NARANJA_PLUS_MODAL_TEXT}</p>
          {motivo && (
            <p className="mt-3 text-[11px] font-bold text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-2.5">
              {motivo}
            </p>
          )}

          <ul className="mt-4 space-y-1.5 text-[11px] font-bold text-slate-600 dark:text-slate-300">
            <li className="flex items-center gap-2"><Cloud size={13} className="text-orange-500" /> Sincronización multi-dispositivo y respaldo en la nube</li>
            <li className="flex items-center gap-2"><Store size={13} className="text-orange-500" /> Tienda digital pública abierta</li>
            <li className="flex items-center gap-2"><Smartphone size={13} className="text-orange-500" /> Subastas en vivo y catálogo compartido</li>
          </ul>

          <p className="mt-4 text-[10px] font-bold text-slate-400">
            Tu POS, facturación, mesas, restaurante e inventario local siguen funcionando al 100% y tus datos locales nunca se borran.
          </p>

          <div className="mt-5 flex gap-2">
            <button
              onClick={() => { setIsOpen(false); navigate('/mi-plan'); }}
              className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 text-white font-black rounded-2xl flex items-center justify-center gap-2"
            >
              <Wallet size={16} /> Ver planes y activar
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="px-4 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl"
            >
              Después
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NaranjaPlusRequiredModal;
